const rideIdOf = (ride) => ride.id || ride.rideId;

/**
 * The trip's stops in driving order, as the live view lists them.
 *
 * One stop per waypoint group — riders sharing a station are one stop — with
 * each rider's pick-up or drop-off at it, the place, whether the driver has
 * been there, and the ETA if it is still ahead.
 *
 * `etas` is the trip's `{tripId}-etas`: one entry per REMAINING stop, each
 * naming the ride steps it covers. They are matched per ride step rather than
 * by position, because the list is rewritten as stops are passed.
 *
 * @param  {object} input
 * @param  {Array<Array<{type: string, ride_id: string, coordinates?: {lat: number, lon: number}}>>} input.waypoints
 * @param  {number} input.currentWaypoint - Index of the stop the driver is heading to
 * @param  {boolean} input.completed - The trip is over: every stop is done
 * @param  {Array<{eta: string, rides: Array<{ride_id: string, type: string}>}>|null} input.etas
 * @param  {Array} input.rides - The day's ride objects, for names and places
 */
export const buildTripStops = ({ waypoints = [], currentWaypoint = 0, completed = false, etas = null, rides = [] }) => {
  const etaByStep = {};
  (etas || []).forEach((entry) =>
    (entry.rides || []).forEach((step) => {
      etaByStep[`${step.ride_id}:${step.type}`] = entry.eta;
    }),
  );
  const rideById = Object.fromEntries(rides.map((r) => [rideIdOf(r), r]));

  // Indexed before filtering: `currentWaypoint` counts the trip's own groups.
  return waypoints
    .map((group, index) => {
      if (!group?.length) return null;
      const steps = group.map((w) => ({ type: w.type, rideId: w.ride_id, rider: rideById[w.ride_id]?.rider || null }));
      const first = group[0];
      const firstRide = rideById[first.ride_id];
      const place = first.type === 'pick-up' ? firstRide?.pickUp : firstRide?.dropOff;
      const done = completed || index < currentWaypoint;
      return {
        index,
        steps,
        lat: first.coordinates?.lat ?? null,
        lng: first.coordinates?.lon ?? null,
        placeName: place?.name || place?.address || null,
        done,
        next: !done && index === currentWaypoint,
        eta: done ? null : group.map((w) => etaByStep[`${w.ride_id}:${w.type}`]).find(Boolean) || null,
      };
    })
    .filter(Boolean);
};
